import React, { useEffect, useState } from "react";
import { RefreshCw, Dices } from "lucide-react";
import { megaApi } from "../lib/api";
import { timeFrom } from "../lib/format";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const Row = ({ k, v, ok, testid }) => (
  <div className="flex items-start justify-between gap-3 px-3.5 py-2 text-[13px]" data-testid={testid}>
    <span className="text-muted-foreground">{k}</span>
    <span className={`font-semibold text-right break-all ${ok === false ? "text-red-400" : ok === true ? "text-emerald-400" : ""}`}>{v}</span>
  </div>
);

// Admin → Settings: live MegaNodes diagnostics (config, catalogue, callbacks seen since last restart).
export default function AdminCasinoStatus({ token }) {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setSt(await megaApi.adminStatus(token)); setErr(""); } catch (e) { setErr(e?.response?.data?.detail || "Failed to load"); } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const cb = st?.callbacks_seen || {};
  const ago = (iso) => (iso ? timeFrom(iso) : "never since restart");
  return (
    <div className="rounded-2xl border border-border bg-card p-6 max-w-lg space-y-3" data-testid="admin-casino-status">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold flex items-center gap-2"><Dices className="h-4 w-4 text-primary" /> Casino Provider (MegaNodes)</h3>
        <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={load} disabled={busy} data-testid="admin-casino-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {err && <div className="text-sm text-red-400" data-testid="admin-casino-error">{err}</div>}
      {st && (
        <div className="rounded-xl border border-border divide-y divide-border">
          <Row k="API token" v={st.configured ? `set (${st.api_token_hint})` : "MISSING"} ok={!!st.configured} testid="casino-api-token" />
          <Row k="Callback token" v={st.callback_token_set ? `set (${st.callback_token_hint})` : "MISSING"} ok={!!st.callback_token_set} testid="casino-cb-token" />
          <Row k="Server outbound IP (whitelist this)" v={st.outbound_ip} testid="casino-outbound-ip" />
          <Row k="Agent" v={st.agent ? `${st.agent.name || "ok"} · credit ${st.agent.point ?? "?"} ${st.agent.currency || ""}` : (st.agent_error || "—")} ok={st.agent ? true : (st.agent_error ? false : undefined)} testid="casino-agent" />
          <Row k="Catalogue" v={`${st.catalogue?.live ?? 0} live · ${st.catalogue?.slots ?? 0} slots · ${st.catalogue?.providers ?? 0} providers${st.catalogue?.age_secs != null ? ` · ${Math.round(st.catalogue.age_secs / 60)} min old` : ""}`} ok={(st.catalogue?.live ?? 0) > 0} testid="casino-catalogue" />
          <Row k="Callback URL to set in panel" v={(st.callbacks?.GetBalance || "").replace(/\/GetBalance$/, "")} testid="casino-cb-url" />
          <Row k="GetBalance calls received" v={`${cb.get_balance ?? 0} · last ${ago(cb.last_get_balance_at)}`} ok={cb.get_balance > 0 ? true : undefined} testid="casino-cb-getbalance" />
          <Row k="Bet/Win calls received" v={`${cb.utransaction ?? 0} · last ${ago(cb.last_utransaction_at)}`} testid="casino-cb-utx" />
          <Row k="Rejected (wrong callback token)" v={`${cb.rejected ?? 0} · last ${ago(cb.last_rejected_at)}`} ok={cb.rejected > 0 ? false : undefined} testid="casino-cb-rejected" />
          <Row k="UserNotFound replies" v={`${cb.user_not_found ?? 0}${cb.last_user_not_found_code ? ` · last code ${cb.last_user_not_found_code}` : ""}`} ok={cb.user_not_found > 0 ? false : undefined} testid="casino-cb-unf" />
        </div>
      )}
      <div className="text-[11px] text-muted-foreground space-y-1">
        <p>Game shows <b>"Connection…"</b> forever ⇒ provider can't reach the callback URL, or the callback token is wrong (see <i>Rejected</i>), or callbacks hit another server (see <i>UserNotFound</i>).</p>
        <p>Launch error <Badge variant="outline" className="text-[10px]">1005/1006</Badge> ⇒ add the outbound IP above to the MegaNodes agent whitelist.</p>
      </div>
    </div>
  );
}
